import classNames from 'classnames';
import { useEffect, useState, useRef } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';

import {
  closeIndividualStudentGradebook,
  getIndividualStudentData,
  selectIndividualStudent,
} from '../../../actions/individualStudentGradebook';
import { getLatestActivityLaunch } from '../../../core/gradebook/launches';
import { SEARCH_TYPES } from '../../../utils/constants';
import MLLoading from '../../MLLoading/MLLoading';
import useSidePanel from '../../SidePanel/hooks/useSidePanel';
import s from './IndividualStudentGradebook.module.css';
import { Body } from './components/Body';
import PerformanceOverview from './components/PerformanceOverview';
import { Header } from './components/Header';
import { useDetectBackNavigation } from './hooks/useDetectBackNavigation';
import { useSearchParams } from './hooks/useSearchParams';
import { IsgProvider } from './store';
import {
  selectIndividualStudentGradebookIsLoading,
  selectIsgData,
  selectStudent,
  selectTimeOnTaskMap,
} from './utils';

const IndividualStudentGradebook = ({
  studentGradebook,
  currentStudent,
  hasStudents,
  settings,
  user,
  courseId,
  getLaunchUrl,
  handleLocalLaunch,
  getStudentReportLink,
  IndividualStudentGradebookIsLoading,
  hasStudentReport,
}) => {
  const dispatch = useDispatch();
  const [params] = useSearchParams();
  const { state: sidePanelState } = useSidePanel();
  useDetectBackNavigation(() => dispatch(closeIndividualStudentGradebook()));
  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const dialogRef = useRef(null);
  const previousCellSelectorRef = useRef(null);

  // Save the focused grid cell's coordinates on mount so we can restore
  // focus when the ISG closes, even if the DOM node was recycled by the
  // virtualizer. The grid uses roving tabindex so the active cell has
  // tabindex="0".
  useEffect(() => {
    const focusedCell = document.querySelector(
      '[data-row-index][data-column-id][tabindex="0"]',
    );
    if (focusedCell) {
      const { rowIndex, columnId } = focusedCell.dataset;
      previousCellSelectorRef.current = `[data-row-index="${rowIndex}"][data-column-id="${columnId}"]`;
    }

    return () => {
      if (previousCellSelectorRef.current) {
        const cell = document.querySelector(previousCellSelectorRef.current);
        if (cell instanceof HTMLElement) {
          cell.focus();
        }
      }
    };
  }, []);

  // Focus the first element after loading completes
  useEffect(() => {
    if (!IndividualStudentGradebookIsLoading && dialogRef.current) {
      const firstFocusable = dialogRef.current.querySelector(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      if (firstFocusable) {
        setTimeout(() => firstFocusable.focus(), 100);
      }
    }
  }, [IndividualStudentGradebookIsLoading]);

  const { searchType, searchPhrase } = useSelector((state) => state.gradebook);
  const [searchTerm, setSearchTerm] = useState(
    searchType === SEARCH_TYPES.ASSIGNMENTS ? searchPhrase : '',
  );

  const studentId = params.get('studentId');

  const [latestActivityLaunch, setLatestActivityLaunch] = useState(null);

  useEffect(() => {
    const fetchTimestamp = async () => {
      const { launch } = await getLatestActivityLaunch(studentId, courseId);
      setLatestActivityLaunch(launch);
    };

    fetchTimestamp();
  }, [studentId, courseId]);

  useEffect(() => {
    if (studentId) {
      dispatch(getIndividualStudentData(courseId, studentId));
    }
  }, [dispatch, courseId, studentId]);

  useEffect(() => {
    const gradebookLink = document.querySelector('#gradebook-navigation');
    const handleCloseGradebook = () =>
      dispatch(closeIndividualStudentGradebook());

    if (gradebookLink) {
      gradebookLink.addEventListener('click', handleCloseGradebook);
    }
    return () => {
      if (gradebookLink) {
        gradebookLink.removeEventListener('click', handleCloseGradebook);
      }
    };
  }, [dispatch]);

  const selectStudent = (student) => {
    dispatch(selectIndividualStudent(student));
  };

  if (!studentId || !hasStudents) {
    return null;
  }

  if (IndividualStudentGradebookIsLoading) {
    return (
      <div
        className={classNames(
          s.dialogWrapper,
          'individual-student-gradebook-scope',
        )}
      >
        <div className={s.spinnerContainer}>
          <MLLoading />
        </div>
      </div>
    );
  }

  const { course, studentPerformanceData } = studentGradebook;
  const hasPerformanceData =
    studentPerformanceData && studentPerformanceData?.length > 0;
  const courseHasNotEnded =
    course?.courseEndDate && new Date(course.courseEndDate) > new Date();
  const hasOrWillHavePerformanceData = hasPerformanceData || courseHasNotEnded;

  return (
    <IsgProvider>
      <div
        ref={dialogRef}
        className={classNames(
          s.dialogWrapper,
          'individual-student-gradebook-scope',
          {
            [s.sidePanelOpen]: sidePanelState.open,
          },
        )}
        data-test-id="individual-student-gradebook-modal"
      >
        <Header
          isBrokenSyncReefGrades={currentStudent?.isBrokenSyncReefGrades}
          user={user}
          gradebookData={studentGradebook}
          latestActivityLaunch={latestActivityLaunch}
          updateSelectedStudent={selectStudent}
          currentStudent={currentStudent}
          setDropdownOpen={setDropdownOpen}
          searchTerm={searchTerm}
        />
        {hasOrWillHavePerformanceData && (
          <PerformanceOverview
            studentName={currentStudent.name}
            course={course}
            categoriesData={studentGradebook.categoriesData}
            performanceData={studentPerformanceData}
          />
        )}
        <Body
          gradebookData={studentGradebook}
          settings={settings}
          getLaunchUrl={getLaunchUrl}
          handleLocalLaunch={handleLocalLaunch}
          getStudentReportLink={getStudentReportLink}
          hasStudentReport={hasStudentReport}
          isDropdownOpen={isDropdownOpen}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
        />
      </div>
    </IsgProvider>
  );
};

function mapStateToProps(state) {
  return {
    IndividualStudentGradebookIsLoading:
      selectIndividualStudentGradebookIsLoading(state),
    timeOnTaskMap: selectTimeOnTaskMap(state),
    studentGradebook: selectIsgData(state),
    currentStudent: selectStudent(state),
  };
}

export default connect(mapStateToProps)(IndividualStudentGradebook);
